import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  memo,
} from "react";
import {
  MdDarkMode,
  MdLightMode,
  MdLogout,
  MdAdd,
  MdClose,
  MdRefresh,
  MdSearch,
  MdCheckCircle,
  MdAttachFile,
} from "react-icons/md";
import { useTheme } from "../contexts/ThemeContext";
import "../styles/Home.css";

function authHeaders(token) {
  const h = { Accept: "application/json; charset=utf-8" };
  if (token) {
    h.Authorization = `Bearer ${token}`;
  }
  return h;
}

function displayBody(msg) {
  const text =
    msg.body_edited != null && msg.body_edited !== ""
      ? msg.body_edited
      : msg.body;
  return text != null && text !== "" ? text : "(kein Text)";
}

const WEEKDAY_SHORT = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

function formatDateLabel(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  const weekday = WEEKDAY_SHORT[date.getDay()];
  const day = date.getDate();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${weekday}. ${day}.${month}`;
}

const BODY_EDIT_DEBOUNCE_MS = 2000;
const INITIAL_PAGE_SIZE = 100;
const VISIBLE_INCREMENT = 20;
const SENTINEL_INDEX_OFFSET = 3; // beim Anzeigen des 18. Elements nächste 20 laden
const FETCH_MORE_THRESHOLD = 90; // ab 90 sichtbaren die nächsten 100 aus der API holen
const SCROLL_LOAD_THRESHOLD_PX = 400; // wie weit vor Ende der Scrollbereich "mehr laden" auslöst

/** Lädt Media per Auth und zeigt Bild/Video/Audio/Dokument-Link. */
function MessageMedia({ apiPath, token, waMessageId, type, filePath }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    if (!apiPath || !token || !waMessageId) return;
    const url = `${apiPath}?action=media&wa_message_id=${encodeURIComponent(
      waMessageId
    )}`;
    let revoked = false;
    fetch(url, { headers: authHeaders(token) })
      .then((res) => {
        if (!res.ok) throw new Error("Media nicht geladen");
        return res.blob();
      })
      .then((blob) => {
        if (!revoked) setBlobUrl(URL.createObjectURL(blob));
      })
      .catch(() => !revoked && setError(true));
    return () => {
      revoked = true;
      setBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    };
  }, [apiPath, token, waMessageId]);

  if (error)
    return (
      <div className="dashboard_center_row_media_placeholder">
        Media nicht verfügbar
      </div>
    );
  if (!blobUrl)
    return <div className="dashboard_center_row_media_placeholder">Laden…</div>;

  const mediaType = (type || "").toLowerCase();
  if (mediaType === "image" || mediaType === "sticker") {
    return (
      <img
        src={blobUrl}
        alt=""
        className="dashboard_center_row_media dashboard_center_row_media_image"
      />
    );
  }
  if (mediaType === "video") {
    return (
      <video
        src={blobUrl}
        controls
        className="dashboard_center_row_media dashboard_center_row_media_video"
      />
    );
  }
  if (mediaType === "audio") {
    return (
      <audio
        src={blobUrl}
        controls
        className="dashboard_center_row_media dashboard_center_row_media_audio"
      />
    );
  }
  const downloadName = (filePath || "").split("/").pop() || "datei";
  const ext = downloadName.includes(".")
    ? downloadName.split(".").pop().toLowerCase()
    : "";
  const downloadLabel = ext ? `${ext.toUpperCase()} herunterladen` : "Datei herunterladen";
  return (
    <a
      href={blobUrl}
      download={downloadName}
      className="dashboard_center_row_media_link">
      {downloadLabel}
    </a>
  );
}

/** Einzelne Notiz-Card: Datum, bearbeitbarer Text (body_edited). Höhe folgt dem Inhalt. */
const DashboardCard = memo(function DashboardCard({
  msg,
  bodyDraft,
  apiPath,
  token,
  onBodyChange,
}) {
  const displayValue =
    bodyDraft !== undefined ? bodyDraft : displayBody(msg);
  const textareaRef = useRef(null);

  const adjustHeight = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  useEffect(() => {
    adjustHeight();
  }, [displayValue, adjustHeight]);

  return (
    <li
      className={`dashboard_center_row ${
        msg.done ? "dashboard_center_row_erledigt" : ""
      }`}>
      <div className="dashboard_center_row_content">
        <span className="dashboard_center_row_date_header">
          {msg.received_at
            ? formatDateLabel(String(msg.received_at).slice(0, 10))
            : "—"}
        </span>
        {msg.file_path && msg.type && msg.type !== "text" && (
          <div className="dashboard_center_row_media_wrap">
            <MessageMedia
              apiPath={apiPath}
              token={token}
              waMessageId={msg.wa_message_id}
              type={msg.type}
              filePath={msg.file_path}
            />
          </div>
        )}
        <div className="dashboard_center_row_text_wrap">
          <textarea
            ref={textareaRef}
            className="dashboard_center_row_text_input"
            value={displayValue}
            onChange={(e) => {
              onBodyChange(msg.wa_message_id, e.target.value);
              requestAnimationFrame(adjustHeight);
            }}
            placeholder="Notiz eingeben…"
            aria-label="Notiztext"
            rows={1}
          />
        </div>
      </div>
    </li>
  );
});

export default function Home({ apiURL, token, onLogout }) {
  const {
    isDark,
    toggleTheme,
    themePreference,
    setThemePreferenceFromServer,
    setOnSettingsChange,
  } = useTheme();
  const baseApiUrl = (apiURL || "https://www.cloud.chatnotes.de").replace(
    /\/$/,
    ""
  );
  const apiPath = `${baseApiUrl}/whatsapp/api.php`;

  const [messages, setMessages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bodyEditedDraftByMessage, setBodyEditedDraftByMessage] = useState({});
  const [animatingOutIds, setAnimatingOutIds] = useState([]);
  const [showCompleted, setShowCompleted] = useState(false);
  const [visibleCount, setVisibleCount] = useState(VISIBLE_INCREMENT);
  const [loadingMore, setLoadingMore] = useState(false);
  const bodySaveTimeoutRef = useRef({});
  const listContainerRef = useRef(null);

  const filteredMessages = useMemo(() => {
    const animatingSet = new Set(animatingOutIds);
    if (showCompleted) {
      return messages;
    }
    return messages.filter(
      (m) => !m.done || animatingSet.has(m.wa_message_id)
    );
  }, [messages, animatingOutIds, showCompleted]);

  const [searchQuery, setSearchQuery] = useState("");

  const searchFilteredMessages = useMemo(() => {
    const q = (searchQuery || "").trim().toLowerCase();
    if (q === "") return filteredMessages;
    const words = q.split(/\s+/).filter((w) => w.length > 0);
    return filteredMessages.filter((msg) => {
      const text = (displayBody(msg) || "").toLowerCase();
      return words.every((word) => text.includes(word));
    });
  }, [filteredMessages, searchQuery]);

  const fetchData = useCallback(
    async (offset = 0, limit = INITIAL_PAGE_SIZE) => {
      if (offset === 0) {
        setLoading(true);
        setError(null);
      }
      try {
        const url =
          limit != null
            ? `${apiPath}?limit=${limit}&offset=${offset}`
            : apiPath;
        const res = await fetch(url, {
          method: "GET",
          headers: authHeaders(token),
        });
        if (res.status === 401 && onLogout) {
          onLogout();
          return;
        }
        const data = await res.json();
        if (offset === 0) {
          setMessages(data.messages || []);
          setVisibleCount(VISIBLE_INCREMENT);
        } else {
          setMessages((prev) => [...prev, ...(data.messages || [])]);
        }
        if (offset === 0) {
          const apiCategories = (data.categories || []).map((c) => ({
            id: String(c.id),
            name: c.name,
          }));
          setCategories(apiCategories);
          const settings = data.settings;
          if (settings && typeof settings === "object") {
            if (
              settings.theme === "light" ||
              settings.theme === "dark" ||
              settings.theme === "system"
            ) {
              setThemePreferenceFromServer(settings.theme);
            }
          }
        }
      } catch (err) {
        setError(err.message);
        if (offset === 0) setMessages([]);
      } finally {
        if (offset === 0) setLoading(false);
      }
    },
    [apiPath, token, onLogout, setThemePreferenceFromServer]
  );

  const loadMoreMessages = useCallback(async () => {
    setLoadingMore(true);
    try {
      const url = `${apiPath}?limit=${INITIAL_PAGE_SIZE}&offset=${messages.length}`;
      const res = await fetch(url, {
        method: "GET",
        headers: authHeaders(token),
      });
      if (!res.ok) return;
      const data = await res.json();
      setMessages((prev) => [...prev, ...(data.messages || [])]);
    } catch (err) {
      console.warn("Weitere Notizen laden:", err.message);
    } finally {
      setLoadingMore(false);
    }
  }, [apiPath, token, messages.length]);

  const saveSettings = useCallback(
    async (partial) => {
      const merged = {
        theme: themePreference,
        ...partial,
      };
      try {
        await fetch(apiPath, {
          method: "PATCH",
          headers: {
            ...authHeaders(token),
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ settings: merged }),
        });
      } catch (err) {
        console.warn("Settings save error:", err.message);
      }
    },
    [apiPath, token, themePreference]
  );

  useEffect(() => {
    setOnSettingsChange(saveSettings);
    return () => setOnSettingsChange(null);
  }, [setOnSettingsChange, saveSettings]);

  useEffect(() => {
    fetchData(0, INITIAL_PAGE_SIZE);
  }, [fetchData]);

  const messagesLengthRef = useRef(messages.length);
  const searchFilteredLengthRef = useRef(searchFilteredMessages.length);
  const visibleCountRef = useRef(visibleCount);
  const loadingMoreRef = useRef(loadingMore);
  messagesLengthRef.current = messages.length;
  searchFilteredLengthRef.current = searchFilteredMessages.length;
  visibleCountRef.current = visibleCount;
  loadingMoreRef.current = loadingMore;

  const onNearBottom = useCallback(() => {
    if (loadingMoreRef.current) return;
    const displayLen = searchFilteredLengthRef.current;
    const totalLoaded = messagesLengthRef.current;
    const vis = visibleCountRef.current;
    if (vis + VISIBLE_INCREMENT <= displayLen) {
      setVisibleCount(vis + VISIBLE_INCREMENT);
      return;
    }
    if (vis >= FETCH_MORE_THRESHOLD && vis + VISIBLE_INCREMENT > totalLoaded) {
      loadMoreMessages().then(() =>
        setVisibleCount((prev) => prev + VISIBLE_INCREMENT)
      );
      return;
    }
    setVisibleCount(Math.min(vis + VISIBLE_INCREMENT, displayLen));
  }, [loadMoreMessages]);

  useEffect(() => {
    const el = listContainerRef.current;
    if (!el || loading || error || filteredMessages.length === 0) return;
    const handleScroll = () => {
      const { scrollTop, clientHeight, scrollHeight } = el;
      if (scrollTop + clientHeight >= scrollHeight - SCROLL_LOAD_THRESHOLD_PX) {
        onNearBottom();
      }
    };
    el.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => el.removeEventListener("scroll", handleScroll);
  }, [onNearBottom, loading, error, searchFilteredMessages.length]);

  const toggleDone = useCallback(
    async (waMessageId) => {
      const msg = messages.find((m) => m.wa_message_id === waMessageId);
      if (!msg) return;
      const nextDone = !msg.done;
      const now = new Date().toISOString().slice(0, 19).replace("T", " ");
      setMessages((prev) =>
        prev.map((m) =>
          m.wa_message_id === waMessageId
            ? { ...m, done: nextDone, progressed_at: now }
            : m
        )
      );
      if (nextDone) setAnimatingOutIds((prev) => [...prev, waMessageId]);
      try {
        const res = await fetch(apiPath, {
          method: "PATCH",
          headers: {
            ...authHeaders(token),
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            wa_message_id: waMessageId,
            done: nextDone,
          }),
        });
        const data = await res.json();
        if (!data.ok) {
          setMessages((prev) =>
            prev.map((m) =>
              m.wa_message_id === waMessageId ? { ...m, done: !nextDone } : m
            )
          );
          setAnimatingOutIds((prev) => prev.filter((id) => id !== waMessageId));
        }
      } catch (err) {
        setMessages((prev) =>
          prev.map((m) =>
            m.wa_message_id === waMessageId ? { ...m, done: !nextDone } : m
          )
        );
        setAnimatingOutIds((prev) => prev.filter((id) => id !== waMessageId));
      }
    },
    [apiPath, token, messages]
  );

  const softDeleteMessage = useCallback(
    async (waMessageId) => {
      try {
        const res = await fetch(apiPath, {
          method: "PATCH",
          headers: {
            ...authHeaders(token),
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ wa_message_id: waMessageId, deleted: true }),
        });
        const data = await res.json();
        if (data.ok) {
          setMessages((prev) =>
            prev.filter((m) => m.wa_message_id !== waMessageId)
          );
        }
      } catch (err) {
        console.warn("Soft-Delete Fehler:", err.message);
      }
    },
    [apiPath, token]
  );

  const handleStrikeAnimationEnd = useCallback((waMessageId) => {
    setAnimatingOutIds((prev) => prev.filter((id) => id !== waMessageId));
  }, []);

  const createNote = useCallback(
    async (body) => {
      const trimmed = (body || "").trim();
      if (trimmed === "") return;
      try {
        const res = await fetch(apiPath, {
          method: "POST",
          headers: {
            ...authHeaders(token),
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            body: trimmed,
            category_id: null,
          }),
        });
        const data = await res.json();
        if (data.ok && data.wa_message_id) {
          const receivedAt =
            data.received_at ||
            new Date().toISOString().slice(0, 19).replace("T", " ");
          setMessages((prev) => [
            {
              wa_message_id: data.wa_message_id,
              body: data.body ?? trimmed,
              body_edited: data.body_edited ?? null,
              category_id: data.category_id ?? null,
              status_quo: data.status_quo ?? null,
              next_step: data.next_step ?? null,
              received_at: receivedAt,
              done: false,
              daily_entries: [],
              type: "text",
              contact_name: "Notiz",
              file_path: null,
              progressed_at: null,
            },
            ...prev,
          ]);
        } else {
          console.warn("Notiz anlegen fehlgeschlagen:", data.error);
        }
      } catch (err) {
        console.warn("Notiz anlegen Fehler:", err.message);
      }
    },
    [apiPath, token]
  );

  const saveBodyEdited = useCallback(
    async (waMessageId, text) => {
      const body_edited =
        text == null || String(text).trim() === ""
          ? null
          : String(text);
      try {
        const res = await fetch(apiPath, {
          method: "PATCH",
          headers: {
            ...authHeaders(token),
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ wa_message_id: waMessageId, body_edited }),
        });
        const data = await res.json();
        if (data.ok) {
          setMessages((prev) =>
            prev.map((m) =>
              m.wa_message_id === waMessageId
                ? { ...m, body_edited: body_edited }
                : m
            )
          );
          setBodyEditedDraftByMessage((prev) => {
            const currentDraft = prev[waMessageId];
            if (currentDraft === body_edited) {
              const next = { ...prev };
              delete next[waMessageId];
              return next;
            }
            return prev;
          });
        }
      } catch (err) {
        console.warn("body_edited speichern:", err.message);
      }
    },
    [apiPath, token]
  );

  const onBodyChange = useCallback(
    (waMessageId, value) => {
      setBodyEditedDraftByMessage((prev) => ({ ...prev, [waMessageId]: value }));
      const ref = bodySaveTimeoutRef.current;
      if (ref[waMessageId] != null) clearTimeout(ref[waMessageId]);
      ref[waMessageId] = setTimeout(() => {
        ref[waMessageId] = null;
        saveBodyEdited(waMessageId, value);
      }, BODY_EDIT_DEBOUNCE_MS);
    },
    [saveBodyEdited]
  );

  useEffect(() => {
    const ref = bodySaveTimeoutRef.current;
    return () => {
      Object.values(ref).forEach((t) => t != null && clearTimeout(t));
    };
  }, []);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const dragDepthRef = useRef(0);

  const IMAGE_MAX_BYTES = 15 * 1024 * 1024;
  const FILE_MAX_BYTES = 50 * 1024 * 1024;
  // Synchron zu cloud.chatnotes.de/whatsapp/api.php ($allowed_ext).
  const ALLOWED_FILE_EXT = useMemo(
    () =>
      new Set([
        "jpg",
        "jpeg",
        "png",
        "gif",
        "webp",
        "heic",
        "heif",
        "bmp",
        "tif",
        "tiff",
        "mp4",
        "webm",
        "mov",
        "m4v",
        "mkv",
        "avi",
        "mp3",
        "wav",
        "ogg",
        "m4a",
        "aac",
        "flac",
        "opus",
        "pdf",
        "doc",
        "docx",
        "xls",
        "xlsx",
        "ppt",
        "pptx",
        "txt",
        "csv",
        "rtf",
        "odt",
        "ods",
        "odp",
        "md",
        "json",
        "xml",
        "zip",
        "rar",
        "7z",
        "gz",
        "tgz",
        "tar",
        "bz2",
        "epub",
        "ics",
        "pages",
        "numbers",
        "key",
      ]),
    []
  );
  const WEB_IMAGE_EXT = useMemo(
    () => new Set(["jpg", "jpeg", "png", "gif", "webp"]),
    []
  );
  const VIDEO_EXT = useMemo(
    () => new Set(["mp4", "webm", "mov", "m4v"]),
    []
  );

  const fileExt = useCallback((file) => {
    const name = (file && file.name) || "";
    const dot = name.lastIndexOf(".");
    if (dot < 0 || dot === name.length - 1) return "";
    return name.slice(dot + 1).toLowerCase();
  }, []);

  const isAllowedMediaFile = useCallback(
    (file) => {
      if (!file) return false;
      return ALLOWED_FILE_EXT.has(fileExt(file));
    },
    [ALLOWED_FILE_EXT, fileExt]
  );

  const prependUploadedNote = useCallback((data, fallbackType) => {
    const receivedAt =
      data.received_at ||
      new Date().toISOString().slice(0, 19).replace("T", " ");
    setMessages((prev) => [
      {
        wa_message_id: data.wa_message_id,
        body: data.body ?? null,
        body_edited: data.body_edited ?? null,
        category_id: data.category_id ?? null,
        status_quo: data.status_quo ?? null,
        next_step: data.next_step ?? null,
        received_at: receivedAt,
        done: false,
        daily_entries: [],
        type: data.type || fallbackType || "image",
        contact_name: data.contact_name || "Notiz",
        file_path: data.file_path || null,
        progressed_at: null,
      },
      ...prev,
    ]);
  }, []);

  const uploadNoteMedia = useCallback(
    async (files) => {
      const incoming = Array.from(files || []);
      const list = incoming.filter(isAllowedMediaFile);
      if (list.length === 0) {
        setUploadError(
          incoming.length === 0
            ? "Bitte eine Datei wählen."
            : "Dieser Dateityp ist nicht erlaubt. Erlaubt sind übliche Bilder, Videos, Audio, Dokumente und Archive (z. B. ZIP)."
        );
        return;
      }
      setUploadError(null);
      setUploadingMedia(true);
      try {
        for (const file of list) {
          const ext = fileExt(file);
          const isWebImage = WEB_IMAGE_EXT.has(ext);
          const isVideo = VIDEO_EXT.has(ext);
          const max = isWebImage ? IMAGE_MAX_BYTES : FILE_MAX_BYTES;
          if (file.size > max) {
            setUploadError(
              isWebImage
                ? `${file.name}: Bild maximal 15 MB.`
                : isVideo
                  ? `${file.name}: Video maximal 50 MB.`
                  : `${file.name}: Datei maximal 50 MB.`
            );
            continue;
          }
          const form = new FormData();
          form.append("file", file);
          const res = await fetch(apiPath, {
            method: "POST",
            headers: authHeaders(token),
            body: form,
          });
          if (res.status === 401 && onLogout) {
            onLogout();
            return;
          }
          const data = await res.json();
          if (data.ok && data.wa_message_id) {
            prependUploadedNote(
              data,
              isVideo ? "video" : isWebImage ? "image" : "document"
            );
          } else {
            setUploadError(data.error || "Upload fehlgeschlagen.");
          }
        }
      } catch (err) {
        setUploadError(err.message || "Upload fehlgeschlagen.");
      } finally {
        setUploadingMedia(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    },
    [apiPath, token, onLogout, isAllowedMediaFile, fileExt, WEB_IMAGE_EXT, VIDEO_EXT, prependUploadedNote]
  );

  useEffect(() => {
    const onPaste = (e) => {
      if (settingsOpen || uploadingMedia) return;
      const items = e.clipboardData && e.clipboardData.files;
      if (!items || items.length === 0) return;
      const media = Array.from(items).filter(isAllowedMediaFile);
      if (media.length === 0) return;
      e.preventDefault();
      uploadNoteMedia(media);
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [settingsOpen, uploadingMedia, isAllowedMediaFile, uploadNoteMedia]);

  return (
    <div
      className={`dashboard${isDragOver ? " dashboard--drop" : ""}`}
      onDragEnter={(e) => {
        e.preventDefault();
        e.stopPropagation();
        dragDepthRef.current += 1;
        if (e.dataTransfer && Array.from(e.dataTransfer.types || []).includes("Files")) {
          setIsDragOver(true);
        }
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        e.stopPropagation();
        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
        if (dragDepthRef.current === 0) setIsDragOver(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        dragDepthRef.current = 0;
        setIsDragOver(false);
        if (settingsOpen || uploadingMedia) return;
        uploadNoteMedia(e.dataTransfer && e.dataTransfer.files);
      }}>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="dashboard_file_input"
        onChange={(e) => uploadNoteMedia(e.target.files)}
        aria-hidden
        tabIndex={-1}
      />
      {isDragOver && (
        <div className="dashboard_drop_overlay" aria-hidden>
          Datei hier ablegen
        </div>
      )}
      <div className="dashboard_center">
        <div className="dashboard_header">
          <header className="dashboard_toprow">
            <button
              type="button"
              className="dashboard_toprow_left_btn"
              onClick={() => {
                if (settingsOpen) setSettingsOpen(false);
                else setSettingsOpen(true);
              }}
              title={settingsOpen ? "Schließen" : "Einstellungen"}
              aria-label={settingsOpen ? "Schließen" : "Einstellungen"}
              aria-expanded={settingsOpen}>
              {settingsOpen ? (
                <MdClose
                  size={18}
                  className="dashboard_toprow_close_icon"
                  aria-hidden
                />
              ) : (
                <span className="dashboard_burger_two" aria-hidden>
                  <span />
                  <span />
                </span>
              )}
            </button>
            {!settingsOpen && (
              <>
                <div className="dashboard_toprow_border" aria-hidden />
                <div className="dashboard_toprow_search_wrap">
                  <MdSearch
                    size={20}
                    className="dashboard_toprow_search_icon"
                    aria-hidden
                  />
                  <input
                    type="search"
                    className="dashboard_toprow_search_input"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Notizen durchsuchen…"
                    aria-label="Notizen durchsuchen"
                  />
                </div>
                <div className="dashboard_toprow_border" aria-hidden />
                <div className="dashboard_toprow_note_actions">
                  <button
                    type="button"
                    className="dashboard_toprow_add_btn"
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    disabled={uploadingMedia}
                    title="Datei hochladen"
                    aria-label="Datei hochladen">
                    <MdAttachFile size={22} aria-hidden />
                  </button>
                  <button
                    type="button"
                    className="dashboard_toprow_add_btn"
                    onClick={() => createNote("Neue Notiz")}
                    title="Neue Notiz"
                    aria-label="Neue Notiz">
                    <MdAdd size={24} aria-hidden />
                  </button>
                </div>
              </>
            )}
          </header>
        </div>
        <div ref={listContainerRef} className="dashboard_center_inner">
          {settingsOpen ? (
            <div className="dashboard_settings_panel">
              <>
                  <button
                    type="button"
                    className="dashboard_settings_btn"
                    onClick={() => {
                      toggleTheme();
                      setSettingsOpen(false);
                    }}
                    aria-label={isDark ? "Light Mode" : "Dark Mode"}>
                    {isDark ? (
                      <MdLightMode size={24} aria-hidden />
                    ) : (
                      <MdDarkMode size={24} aria-hidden />
                    )}
                    <span className="dashboard_settings_btn_label">
                      {isDark ? "Light Mode" : "Dark Mode"}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="dashboard_settings_btn"
                    onClick={() => {
                      setShowCompleted((s) => !s);
                      setSettingsOpen(false);
                    }}
                    aria-label={showCompleted ? "Erledigte ausblenden" : "Erledigte einblenden"}>
                    <MdCheckCircle size={24} aria-hidden />
                    <span className="dashboard_settings_btn_label">
                      {showCompleted ? "Erledigte ausblenden" : "Erledigte einblenden"}
                    </span>
                  </button>
                  {onLogout && (
                    <button
                      type="button"
                      className="dashboard_settings_btn dashboard_settings_btn_logout"
                      onClick={() => {
                        onLogout();
                        setSettingsOpen(false);
                      }}
                      aria-label="Abmelden">
                      <MdLogout size={24} aria-hidden />
                      <span className="dashboard_settings_btn_label">
                        Abmelden
                      </span>
                    </button>
                  )}
              </>
            </div>
          ) : loading ? (
            <div className="dashboard_center_loading">Laden…</div>
          ) : error ? (
            <div className="dashboard_center_error">{error}</div>
          ) : (
            <>
              {uploadError && (
                <div className="dashboard_upload_error" role="alert">
                  {uploadError}
                </div>
              )}
              {uploadingMedia && (
                <div className="dashboard_center_loading_more" aria-busy="true">
                  Datei wird hochgeladen…
                </div>
              )}
              {searchFilteredMessages.length === 0 ? (
                <div className="dashboard_center_empty">
                  {searchQuery.trim() ? "Keine Notizen passen zur Suche." : "Keine Nachrichten."}
                </div>
              ) : (
                <>
                  <ul className="dashboard_center_list">
                    {searchFilteredMessages
                      .slice(0, visibleCount)
                      .map((msg) => (
                        <DashboardCard
                          key={msg.wa_message_id}
                          msg={msg}
                          bodyDraft={bodyEditedDraftByMessage[msg.wa_message_id]}
                          apiPath={apiPath}
                          token={token}
                          onBodyChange={onBodyChange}
                        />
                      ))}
                  </ul>
                  {loadingMore && (
                    <div className="dashboard_center_loading_more" aria-busy="true">
                      <MdRefresh
                        size={32}
                        className="dashboard_center_loading_spinner"
                        aria-hidden
                      />
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
